import { Module, VuexModule, Action, Mutation } from "vuex-module-decorators";
import { v4 } from "uuid";
import moment, { Moment } from "moment";
import {
	AnonymousCredential,
	BlobServiceClient,
	BlockBlobClient,
	BlockBlobParallelUploadOptions,
	newPipeline
} from "@azure/storage-blob";
import { fmtMsg, MessageHelper, store, ServerExceptionCode, NotificationType } from "commonui";
import { LessonPlanTemplateActionStatus } from "@/util/enum";
import { ILessonPlanTemplateModel } from "@/models/lesson-plan";
import { ILessonPlanTemplateService, LessonPlanTemplateService, SignatureResoureModel } from "@/services/lesson-plan";
import { LessonPlanLocale } from "@/locales/localeid";
import FileSaver from "file-saver";

function showErrorMessage({ error_code, error_description }): boolean {
	const errorDescriptionPrefix = "Error:";
	const regionNames = error_description.length > 0 ? error_description[0].replace(errorDescriptionPrefix, "") : "";
	switch (error_code) {
		case ServerExceptionCode.TargetHasExistedException:
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(LessonPlanLocale.LessonPlanVersionNameExistsMessage)
			);
			return false;
		case ServerExceptionCode.LessonPlanVersionUsedException:
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(LessonPlanLocale.LessonPlanVersionRegionStatusErrorMessage, { regionNames })
			);
			return false;
		default:
			return true;
	}
}

@Module({ namespaced: true, name: "LessonPlanTemplateState", store: store, dynamic: true })
export default class LessonPlanTemplateState extends VuexModule {
	lessonPlanTemplateService: ILessonPlanTemplateService = LessonPlanTemplateService();
	lessonPlanTemplates: ILessonPlanTemplateModel[] = [];
	lessonPlanTemplate: ILessonPlanTemplateModel = {} as ILessonPlanTemplateModel;
	currentLessonPlanTemplate: ILessonPlanTemplateModel = {} as ILessonPlanTemplateModel;
	currentLessonPlanTemplateAction: LessonPlanTemplateActionStatus = LessonPlanTemplateActionStatus.Add;
	loading: boolean = false;
	sasURL: SignatureResoureModel | null = null;
	sasURLFetchedAt: Moment | null = null;
	downloadSASURL: SignatureResoureModel | null = null;
	downloadSASURLFetchAt: Moment | null = null;
	buildinContainer: string = "buildin-template-file";
	buildinLessonPlanTemplate: ILessonPlanTemplateModel = {
		id: this.buildinContainer,
		headerRowRange: "4",
		teachingPlanRowRange: "5",
		transitionRowRange: "6",
		templateFileName: "GS2020_LessonPlanTemplate.xlsx"
	};

	@Mutation
	setSasURL(value: SignatureResoureModel) {
		this.sasURL = value;
		this.sasURLFetchedAt = moment();
	}

	@Mutation
	setDownloadSasURL(value: SignatureResoureModel) {
		this.downloadSASURL = value;
		this.downloadSASURLFetchAt = moment();
	}

	@Mutation
	setLessonPlanTemplates(lessonPlanTemplates: ILessonPlanTemplateModel[]): void {
		this.lessonPlanTemplates = lessonPlanTemplates;
	}

	@Mutation
	setLessonPlanTemplateAction(action: LessonPlanTemplateActionStatus): void {
		this.currentLessonPlanTemplateAction = action;
	}

	@Mutation
	setCurrentLessonPlanTemplate(lessonPlanTemplate: ILessonPlanTemplateModel): void {
		this.lessonPlanTemplate = lessonPlanTemplate;
		this.currentLessonPlanTemplate = lessonPlanTemplate;
	}

	@Action
	async generateBlockBlobClient(args: { sasURL: string; container: string; path: string }): Promise<BlockBlobClient> {
		const { sasURL, container, path } = args;
		const pipeline = newPipeline(new AnonymousCredential(), {
			retryOptions: { maxTries: 4 } // Retry options
		});
		const serviceClient = new BlobServiceClient(sasURL, pipeline);
		const containerClient = serviceClient.getContainerClient(container);
		return containerClient.getBlockBlobClient(path);
	}

	@Action
	async getSasURL() {
		// gets sas URL for uploads
		if (!this.sasURL || moment(this.sasURLFetchedAt).diff(moment(), "h") > 1) {
			// fetch sas url
			this.setSasURL(await this.lessonPlanTemplateService.getUploadSasURL());
		}
	}

	@Action
	async getDownloadSasURL() {
		// gets sas URL for uploads
		if (!this.downloadSASURL || moment(this.downloadSASURLFetchAt).diff(moment(), "h") > 1) {
			// fetch sas url
			this.setDownloadSasURL(await this.lessonPlanTemplateService.getDownloadSasURL());
		}
	}

	@Action
	getFilePath(template: ILessonPlanTemplateModel): string {
		const fileName = template.templateFile ? template.templateFile.name : template.templateFileName;
		return `${template.id}/${fileName}`;
	}

	@Action
	async uploadFile(template: ILessonPlanTemplateModel) {
		// gets the sas url
		await this.getSasURL();
		const blobPath = await this.getFilePath(template);
		const blockBlobClient = await this.generateBlockBlobClient({
			sasURL: this.sasURL?.url as string,
			container: this.sasURL?.name as string,
			path: blobPath
		});
		const parallelOptions: BlockBlobParallelUploadOptions = {
			concurrency: 5,
			onProgress: ({ loadedBytes }) => {
				// console.log(loadedBytes);
			}
		};
		await blockBlobClient.uploadData(template.templateFile as File, parallelOptions);
	}

	@Action
	async downloadFile(template: ILessonPlanTemplateModel): Promise<File | undefined> {
		// gets the sas url
		await this.getDownloadSasURL();
		const blobPath = await this.getFilePath(template);
		const blockBlobClient = await this.generateBlockBlobClient({
			sasURL: this.downloadSASURL?.url as string,
			container: this.downloadSASURL?.name as string,
			path: blobPath
		});
		const request = await blockBlobClient.download();
		const blob = await request.blobBody;
		if (blob) {
			const b: any = blob;
			//A Blob is almost a File - it's just missing the two properties below which we will add
			b.lastModifiedDate = new Date();
			b.name = template.templateFileName;
			return blob as File;
		}
		return undefined;
	}

	@Action
	async deleteFile(template: ILessonPlanTemplateModel) {
		// gets the sas url
		await this.getSasURL();
		const blobPath = await this.getFilePath(template);
		const blockBlobClient = await this.generateBlockBlobClient({
			sasURL: this.sasURL?.url as string,
			container: this.sasURL?.name as string,
			path: blobPath
		});
		await blockBlobClient.deleteIfExists();
	}

	@Action
	async fetchLessonPlanTemplates(): Promise<void> {
		this.refreshLessonPlanTemplates();
	}

	@Action
	async loadBuildinLessonPlanTemplate(): Promise<void> {
		this.setLoading(true);
		const templateFile = await this.downloadFile(this.buildinLessonPlanTemplate);
		this.setCurrentLessonPlanTemplate({ ...this.buildinLessonPlanTemplate, templateFile });
		this.setLoading(false);
	}

	@Action
	async fetchLessonPlanTemplate({ templateId }: { templateId: string }): Promise<void> {
		this.setLoading(true);
		const lessonPlanTemplate: ILessonPlanTemplateModel = await this.lessonPlanTemplateService.getLessonPlanTemplate(
			templateId
		);
		const templateFile = await this.downloadFile(lessonPlanTemplate);
		this.setCurrentLessonPlanTemplate({ ...lessonPlanTemplate, templateFile });
		this.setLoading(false);
	}

	@Action
	async refreshLessonPlanTemplates(needSetLoading: boolean = true): Promise<void> {
		if (needSetLoading) {
			this.setLoading(true);
		}
		const lessonPlanTemplates: ILessonPlanTemplateModel[] = await this.lessonPlanTemplateService.getLessonPlanTemplates();
		this.setLessonPlanTemplates(lessonPlanTemplates);
		if (needSetLoading) {
			this.setLoading(false);
		}
	}

	@Action
	async saveLessonPlanTemplateData(action: LessonPlanTemplateActionStatus): Promise<boolean> {
		const { templateFile, ...rest } = this.currentLessonPlanTemplate;
		const templateId = action == LessonPlanTemplateActionStatus.Edit ? rest.id : v4();
		let result = true;
		const saveRequest: (action: LessonPlanTemplateActionStatus) => Promise<any> = (
			action: LessonPlanTemplateActionStatus
		) => {
			return action == LessonPlanTemplateActionStatus.Edit
				? this.lessonPlanTemplateService.editLessonPlanTemplate(templateId as string, rest)
				: this.lessonPlanTemplateService.createLessonPlanTemplate({ ...rest, id: templateId });
		};
		try {
			this.setLoading(true);
			//step1: upload file
			if (templateFile) {
				const template = { ...this.currentLessonPlanTemplate, id: templateId };
				//first delete old template file in azure
				action == LessonPlanTemplateActionStatus.Edit && (await this.deleteFile(template));
				await this.uploadFile(template);
			}
			//step2: save template into content db
			await saveRequest(action).catch(({ error_code, error_description }) => {
				result = showErrorMessage({ error_code, error_description });
			});
			this.setLoading(false);
		} catch (error) {
			this.setLoading(false);
			result = false;
		}
		return result;
	}

	@Action
	async deleteLessonPlanTemplate(lessonPlanTemplate: ILessonPlanTemplateModel): Promise<void> {
		this.setLoading(true);
		await this.lessonPlanTemplateService.deleteLessonPlanTemplate(lessonPlanTemplate.id as string);
		this.refreshLessonPlanTemplates(false);
		this.setLoading(false);
	}

	@Action
	async downloadBuildinLessonPlanTemplate(): Promise<void> {
		this.setLoading(true);
		const templateFile = await this.downloadFile(this.buildinLessonPlanTemplate);
		templateFile && FileSaver.saveAs(templateFile, `${this.buildinLessonPlanTemplate.templateFileName}`);
		this.setLoading(false);
	}

	@Mutation
	setLoading(loading: boolean): void {
		this.loading = loading;
	}
}
